import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { sprintf } from "sprintf-js"
import { TranslationKey } from "src/types"

// Returns a union of translation keys for either the `common` or `legal` namespace - defaults to `common`

/**
 * React hook for translations. Returns a function that accepts a translation key and options and returns a translated
 * string in the current language.
 *
 * By default, translation keys will be looked up in the `common` namespace. If you want to look up a translation key
 * in a different namespace, you can pass the namespace in the options object like this: `{ ns: 'myNamespace' }`.
 *
 * Interpolation for the translation key is supported with the following syntax:
 * - the i18next format uses `{{key}}`
 * - the sprintf format uses `%s`
 *
 * When using the i18next format, the options should be a key-value object.
 * When using the sprintf format, the options should be an array of strings.
 *
 * @example
 * // public/locales/en/common.json
 * // {
 * //   "greeting": "Hello",
 * //   "greeting_name": "Hello, {{name}}!",
 * //   "greeting_name_sprintf": "Hello, %s!"
 * // }
 * // public/locales/en/legal.json
 * // {
 * //   "greeting": "Dear"
 * // }
 * const t = useTranslate(); // namespace: 'common'
 * const tLegal = useTranslate(TranslationNamespace.Legal); // namespace: 'legal'
 *
 * t('greeting'); // 'Hello'
 * t('greeting_name', { name: 'John' }); // => 'Hello, John!'
 * t('greeting_x', ['John']); // => 'Hello, John!'
 * tLegal('greeting'); // => 'Dear'
 */
export enum Namespaces {
  Default = "default",
  Common = "common",
}
export default function useTranslate<T extends Namespaces>(
  ns?: T,
): (
  key: TranslationKey<T>,
  options?: { [key: string]: unknown } | Array<string | number>,
) => string {
  // Default to the `common` namespace the namespace is `legal` or `undefined`
  const { t } = useTranslation(ns)

  return useCallback(
    (key, options) => {
      // If options is an array, use sprintf for interpolation
      if (Array.isArray(options)) {
        return sprintf(t(key), ...options)
      }
      // Otherwise, use the i18next interpolation function
      return t(key, options)
    },
    [t],
  )
}
